import { capitalize } from 'lodash';

import { ImageSaasEscrow, ImageContinuity, ImageCustodian, ImageEscrow, ImageBackups } from 'src/components/Svg/Images';
import { SuggestedChangeChangeTypeEnum } from 'src/graphql/schema';

import CompanyIcon from '../ui/CompanyIcon';

import type {
  IPayload,
  IPart,
  IEscrowInvitationSentPayload,
  ICustodianProviderPayload,
  IEscrowAssetPayload,
  IContinuityProviderPayload,
  ILogType,
  ISuggestedChangePayload,
  IBackupPayload,
  ICompanyPayload,
  ILegacyRepoPayload,
  IEscrowPayload,
  IAppPayload,
  IAppRequestPayload,
} from './types';

type ContentCtx = { escrowName: string; payload: IPayload; entryHref: string | null };
type ContentFn = (ctx: ContentCtx) => string | IPart[] | null;
type ContentEntry = ContentFn | Partial<Record<ILogType | 'default', ContentFn>>;

const entryBaseMap: Record<string, string> = {
  software: '/software-escrows',
  saas: '/saas-escrows',
  continuity: '/continuity-escrows',
  CustodianProfile: '/custodian-profiles',
  Backup: '/backups',
  App: '/applications',
};

const buildEntryHref = (parentId: string, entryType: string | undefined): string | null => {
  if (!entryType) return null;
  const base = entryBaseMap[entryType];
  return base ? `${base}/${parentId}` : null;
};

const linkPart = (text: string, href: string | null): IPart => (href ? { text, href } : { text });

const suggestedChangeTypeMap = {
  [SuggestedChangeChangeTypeEnum.AgreementSuggestedChange]: 'Agreement information',
  [SuggestedChangeChangeTypeEnum.BeneficiarySuggestedChange]: 'Beneficiary information',
  [SuggestedChangeChangeTypeEnum.DepositorSuggestedChange]: 'Depositor information',
  [SuggestedChangeChangeTypeEnum.DepositSuggestedChange]: 'Deposited assets',
  [SuggestedChangeChangeTypeEnum.NotificationSuggestedChange]: 'Technical information',
  [SuggestedChangeChangeTypeEnum.EnvironmentSuggestedChange]: 'Environment',
};

const UNTITLED_APP_NAME = 'Untitled application';

const productTypeMap: Record<string, string> = {
  custodian_profile: 'Custodian profile',
  software_escrow: 'Software escrow',
  continuity_escrow: 'Continuity escrow',
  saas_escrow: 'Saas escrow',
  backup: 'Backup',
};

const contentMap: Partial<Record<string, ContentEntry>> = {
  company_updated: () => 'Company details updated',
  company_ownership_transferred: ({ payload }) => {
    const { user_name } = payload as ICompanyPayload;
    return `Account ownership transferred to ${user_name}`;
  },
  user_updated: ({ payload }) => {
    const { user_name } = payload as ICompanyPayload;
    return `User permissions for ${user_name} updated`;
  },
  user_email_changed: () => 'Email address updated',
  user_invited: ({ payload }) => {
    const { user_name } = payload as ICompanyPayload;
    return `${user_name} invited to join account as a team member`;
  },
  user_removed: ({ payload }) => {
    const { user_name } = payload as ICompanyPayload;
    return `${user_name} removed from account`;
  },
  integration_created: ({ payload }) => {
    const { integration_name } = payload as ICompanyPayload;
    return `Integration ${integration_name} connected`;
  },
  integration_disconnected: ({ payload }) => {
    const { integration_name } = payload as ICompanyPayload;
    return `Integration ${integration_name} disconnected`;
  },
  integration_reconnected: ({ payload }) => {
    const { integration_name } = payload as ICompanyPayload;
    return `Integration ${integration_name} reconnected`;
  },
  integration_removed: ({ payload }) => {
    const { integration_name } = payload as ICompanyPayload;
    return `Integration ${integration_name} removed`;
  },
  integration_member_created: ({ payload }) => {
    const { integration_name, member_name } = payload as ICompanyPayload;
    return `${member_name} granted access to integration ${integration_name}`;
  },
  integration_member_removed: ({ payload }) => {
    const { integration_name, member_name } = payload as ICompanyPayload;
    return `${member_name}'s access to integration ${integration_name} removed`;
  },
  custom_integration_created: ({ payload }) => {
    const { integration_name } = payload as ICompanyPayload;
    return `Integration ${integration_name} created`;
  },
  custom_integration_updated: ({ payload }) => {
    const { integration_name } = payload as ICompanyPayload;
    return `Integration ${integration_name} credentials updated`;
  },
  sharefile_integration_created: () => 'Sharefile folder requested',
  billing_address_updated: () => 'Organization billing address updated',
  payment_source_added: () => 'New payment method added',
  payment_source_updated: () => 'Payment method updated',
  payment_source_deleted: () => 'Payment method removed',
  payment_source_expiring: () => 'Account payment method is expiring soon',
  payment_source_expired: () => 'Payment method has expired',
  coupon_applied: () => 'Coupon applied to subscription',
  exclusive_offer_requested: () => 'Discount offer requested',
  subscription_cancellation_requested: () => 'Subscription cancellation requested',
  subscription_cancellation_scheduled: () =>
    'Subscription cancellation scheduled at next renewal subject to account terms',
  subscription_downgraded: () => 'Subscription downgraded to free account',
  app_created: ({ payload, entryHref }) => {
    const { app_name = UNTITLED_APP_NAME } = payload as IAppPayload;
    return [{ text: 'Application ' }, linkPart(app_name, entryHref), { text: ' created' }];
  },
  app_updated: ({ payload, entryHref }) => {
    const { app_name = UNTITLED_APP_NAME } = payload as IAppPayload;
    return [{ text: 'Application ' }, linkPart(app_name, entryHref), { text: ' details updated' }];
  },
  app_membership_created: ({ payload, entryHref }) => {
    const { app_name = UNTITLED_APP_NAME, member_name } = payload as IAppPayload;
    return [{ text: `${member_name} given access to application ` }, linkPart(app_name, entryHref)];
  },
  app_membership_updated: ({ payload, entryHref }) => {
    const { app_name = UNTITLED_APP_NAME, member_name } = payload as IAppPayload;
    return [{ text: `${member_name} permissions for ` }, linkPart(app_name, entryHref), { text: ' updated' }];
  },
  app_membership_removed: ({ payload, entryHref }) => {
    const { app_name = UNTITLED_APP_NAME, member_name } = payload as IAppPayload;
    return [{ text: `${member_name}'s access to application ` }, linkPart(app_name, entryHref), { text: ' removed' }];
  },
  product_linked: ({ payload, entryHref }) => {
    const { app_name = UNTITLED_APP_NAME, product_name, product_type } = payload as IAppPayload;
    const productTypeLabel = (product_type && productTypeMap[product_type]) ?? product_type;
    return [{ text: `${productTypeLabel} ${product_name} linked to application ` }, linkPart(app_name, entryHref)];
  },
  app_removed: ({ payload }) => {
    const { app_name = UNTITLED_APP_NAME } = payload as IAppPayload;
    return `Application ${app_name} removed`;
  },
  app_request_created: ({ payload, entryHref }) => {
    const {
      party_type,
      app_name = UNTITLED_APP_NAME,
      vendor_name,
      requester_company_name,
    } = payload as IAppRequestPayload;

    if (party_type === 'requester') return `Vendor request sent to ${vendor_name}`;
    if (party_type === 'vendor')
      return [{ text: `${requester_company_name} sent a vendor request for ` }, linkPart(app_name, entryHref)];
    return null;
  },
  app_request_accepted: ({ payload, entryHref }) => {
    const {
      party_type,
      app_name = UNTITLED_APP_NAME,
      vendor_name,
      requester_company_name,
    } = payload as IAppRequestPayload;

    if (party_type === 'requester')
      return [{ text: `${vendor_name} accepted your request and set up ` }, linkPart(app_name, entryHref)];
    if (party_type === 'vendor') return `Vendor request from ${requester_company_name} accepted`;
    return null;
  },
  app_request_declined: ({ payload }) => {
    const { party_type, vendor_name, requester_company_name } = payload as IAppRequestPayload;

    if (party_type === 'requester') return `${vendor_name} declined the vendor request`;
    if (party_type === 'vendor') return `Vendor request from ${requester_company_name} declined`;
    return null;
  },
  escrow_created: ({ escrowName, payload, entryHref }) => {
    const { app_name = UNTITLED_APP_NAME } = payload as IEscrowPayload;
    return [linkPart(escrowName, entryHref), { text: ` protection added to application ${app_name}` }];
  },
  escrow_updated: ({ escrowName, payload, entryHref }) => [
    { text: `${capitalize(payload.escrow_type)} escrow ` },
    linkPart(escrowName, entryHref),
    { text: ' updated' },
  ],
  escrow_signed: ({ escrowName, entryHref }) => [
    { text: 'Escrow agreement ' },
    linkPart(escrowName, entryHref),
    { text: ' signed by all parties' },
  ],
  escrow_activated: ({ escrowName, entryHref }) => [
    { text: 'Escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' activated' },
  ],
  escrow_removed: ({ escrowName, entryHref }) => [
    { text: 'Escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' removed' },
  ],
  escrow_pending_activation: {
    continuity: ({ escrowName, entryHref }) => [
      { text: 'Activation request for escrow ' },
      linkPart(escrowName, entryHref),
      { text: ' submitted' },
    ],
    default: ({ escrowName, entryHref }) => [
      { text: 'Escrow ' },
      linkPart(escrowName, entryHref),
      { text: ' activation initiated' },
    ],
  },
  escrow_invitation_sent: ({ payload, entryHref }) => {
    const { invited_user_name, inviting_company_name, invited_company_name, party_type } =
      payload as IEscrowInvitationSentPayload;

    if (!invited_user_name) return null;

    const escrowName = (
      party_type === 'depositor'
        ? [invited_company_name, inviting_company_name]
        : [inviting_company_name, invited_company_name]
    )
      .filter(Boolean)
      .join(' <> ');
    return [{ text: `Invitation sent to ${invited_user_name} for ` }, linkPart(escrowName, entryHref)];
  },
  escrow_invitation_accepted: ({ escrowName, entryHref }) => [
    { text: 'Invitation to ' },
    linkPart(escrowName, entryHref),
    { text: ' accepted' },
  ],
  escrow_invitation_rejected: ({ entryHref, escrowName }) => [
    { text: 'Invitation to ' },
    linkPart(escrowName, entryHref),
    { text: ' rejected' },
  ],
  escrow_membership_created: ({ payload, escrowName, entryHref }) => {
    const { member_name } = payload as IEscrowPayload;
    return [{ text: `${member_name} added as a member of escrow ` }, linkPart(escrowName, entryHref)];
  },
  escrow_membership_updated: ({ payload, escrowName, entryHref }) => {
    const { member_name } = payload as IEscrowPayload;
    return [
      { text: `Member permission for ${member_name} on escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' updated' },
    ];
  },
  escrow_membership_removed: ({ payload, escrowName, entryHref }) => {
    const { member_name } = payload as IEscrowPayload;
    return [{ text: `${member_name} removed as a member from escrow ` }, linkPart(escrowName, entryHref)];
  },
  agreement_generated: ({ escrowName, entryHref }) => [
    { text: 'Agreement for escrow ' },
    linkPart(escrowName, entryHref),
    { text: " generated and sent for Codekeeper's review" },
  ],
  agreement_sent_for_review_beneficiary: ({ escrowName, entryHref }) => [
    { text: 'Agreement for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' sent for review' },
  ],
  agreement_sent_for_review_depositor: ({ escrowName, entryHref }) => [
    { text: 'Agreement for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' sent for review' },
  ],
  agreement_resent_for_review_depositor: ({ escrowName, entryHref }) => [
    { text: 'Agreement for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' resent to for review' },
  ],
  agreement_resent_for_review_beneficiary: ({ escrowName, entryHref }) => [
    { text: 'Agreement for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' resent to for review' },
  ],
  agreement_approved: ({ escrowName, entryHref }) => [
    { text: 'Agreement for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' approved and submitted for signature' },
  ],
  signature_signed: ({ escrowName, payload, entryHref }) => {
    const { signatory_name } = payload as IEscrowPayload;
    return [
      { text: 'Agreement for escrow ' },
      linkPart(escrowName, entryHref),
      { text: ` signed on behalf of ${signatory_name}` },
    ];
  },
  signatures_requested: ({ escrowName, entryHref }) => [
    { text: 'Agreement for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' sent for signature' },
  ],
  suggested_change_created: ({ escrowName, payload, entryHref }) => {
    const { change_type } = payload as ISuggestedChangePayload;
    return [
      { text: `Change to ${suggestedChangeTypeMap[change_type]} on escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' requested' },
    ];
  },
  suggested_change_accepted: ({ escrowName, payload, entryHref }) => {
    const { change_type } = payload as ISuggestedChangePayload;
    return [
      { text: `Change to ${suggestedChangeTypeMap[change_type]} on escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' accepted' },
    ];
  },
  suggested_change_rejected: ({ escrowName, payload, entryHref }) => {
    const { change_type } = payload as ISuggestedChangePayload;
    return [
      { text: `Change to ${suggestedChangeTypeMap[change_type]} on escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' rejected' },
    ];
  },
  suggested_change_admin_rejected: ({ escrowName, payload, entryHref }) => {
    const { change_type } = payload as ISuggestedChangePayload;
    return [
      { text: `Change to ${suggestedChangeTypeMap[change_type]} on escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' rejected' },
    ];
  },
  suggested_change_applied: ({ escrowName, payload, entryHref }) => {
    const { change_type } = payload as ISuggestedChangePayload;
    return [
      { text: `Change to ${suggestedChangeTypeMap[change_type]} on escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' applied' },
    ];
  },
  escrow_termination_requested: ({ escrowName, entryHref }) => [
    { text: 'Termination for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' requested' },
  ],
  escrow_termination_accepted: ({ escrowName, entryHref }) => [
    { text: 'Termination request for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' accepted' },
  ],
  escrow_termination_rejected: ({ escrowName, entryHref }) => [
    { text: 'Termination request for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' rejected' },
  ],
  escrow_termination_admin_accepted: ({ escrowName, entryHref }) => [
    { text: 'Termination of escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' approved' },
  ],
  escrow_termination_admin_declined: ({ escrowName, entryHref }) => [
    { text: 'Termination of escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' declined' },
  ],
  escrow_terminated: ({ escrowName, entryHref }) => [
    { text: 'Notice period ended: Escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' terminated' },
  ],
  asset_created: ({ payload, escrowName, entryHref }) => {
    const { asset_name } = payload as IEscrowAssetPayload;
    return [{ text: `Asset ${asset_name} added to escrow ` }, linkPart(escrowName, entryHref)];
  },
  asset_removed: ({ payload, escrowName, entryHref }) => {
    const { asset_name } = payload as IEscrowAssetPayload;
    return [{ text: `Asset ${asset_name} removed from escrow ` }, linkPart(escrowName, entryHref)];
  },
  asset_connected: ({ payload, escrowName, entryHref }) => {
    const { asset_name } = payload as IEscrowAssetPayload;
    return [{ text: `Asset ${asset_name} in escrow ` }, linkPart(escrowName, entryHref), { text: ' sync connected' }];
  },
  asset_disconnected: ({ payload, escrowName, entryHref }) => {
    const { asset_name } = payload as IEscrowAssetPayload;
    return [
      { text: `Asset ${asset_name} in escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' sync disconnected' },
    ];
  },
  legacy_repo_migrated: ({ payload, escrowName, entryHref }) => {
    const { repo_name } = payload as ILegacyRepoPayload;
    return [
      { text: `Repository ${repo_name} in escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' migrated to OAuth integration' },
    ];
  },
  escrow_reports_disabled: ({ escrowName, entryHref }) => [
    { text: 'Automated reporting for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' disabled' },
  ],
  escrow_verified_addon_enabled: ({ escrowName, entryHref }) => [
    { text: 'Verified Add-on for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' enabled' },
  ],
  escrow_certified_addon_requested: ({ escrowName, entryHref }) => [
    { text: 'Certified Add-on for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' requested' },
  ],
  continuity_escrow_report_generated: ({ escrowName, entryHref }) => [
    { text: 'Verified report for escrow ' },
    linkPart(escrowName, entryHref),
    { text: ' generated' },
  ],
  continuity_provider_created: ({ payload, escrowName, entryHref }) => {
    const { provider_platform } = payload as IContinuityProviderPayload;
    return [{ text: `Provider ${provider_platform} added to escrow ` }, linkPart(escrowName, entryHref)];
  },
  continuity_provider_removed: ({ payload, escrowName, entryHref }) => {
    const { provider_platform } = payload as IContinuityProviderPayload;
    return [{ text: `Provider ${provider_platform} removed from escrow ` }, linkPart(escrowName, entryHref)];
  },
  continuity_provider_confirmed: ({ payload, escrowName, entryHref }) => {
    const { provider_platform } = payload as IContinuityProviderPayload;
    return [
      { text: `Codekeeper's access to provider ${provider_platform} in escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' confirmed' },
    ];
  },
  continuity_provider_access_issues: ({ payload, escrowName, entryHref }) => {
    const { provider_platform } = payload as IContinuityProviderPayload;
    return [
      { text: `Access to provider ${provider_platform} in escrow ` },
      linkPart(escrowName, entryHref),
      { text: ' disrupted' },
    ];
  },
  custodian_profile_created: ({ payload, entryHref }) => {
    const { custodian_profile_name: name, app_name = UNTITLED_APP_NAME } = payload as ICustodianProviderPayload;
    return [linkPart(name, entryHref), { text: ` protection added to application ${app_name}` }];
  },
  custodian_profile_sent_for_review: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Custodian profile ' }, linkPart(name, entryHref), { text: " submitted for Codekeeper's review" }];
  },
  custodian_profile_removed: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Custodian profile ' }, linkPart(name, entryHref), { text: ' removed' }];
  },
  custodian_profile_activated: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Custodian profile ' }, linkPart(name, entryHref), { text: ' activated' }];
  },
  custodian_profile_terminated: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Custodian profile ' }, linkPart(name, entryHref), { text: ' terminated' }];
  },
  custodian_termination_requested: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Termination of custodian profile ' }, linkPart(name, entryHref), { text: ' requested' }];
  },
  custodian_termination_rejected: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Termination of custodian profile ' }, linkPart(name, entryHref), { text: ' rejected' }];
  },
  custodian_asset_created: ({ payload, entryHref }) => {
    const { custodian_profile_name: name, asset_name } = payload as ICustodianProviderPayload;
    return [{ text: `Asset ${asset_name} added to custodian profile ` }, linkPart(name, entryHref)];
  },
  custodian_asset_updated: ({ payload, entryHref }) => {
    const { custodian_profile_name: name, asset_name } = payload as ICustodianProviderPayload;
    return [
      { text: `Access to asset ${asset_name} in custodian profile ` },
      linkPart(name, entryHref),
      {
        text: ' updated',
      },
    ];
  },
  custodian_asset_removed: ({ payload, entryHref }) => {
    const { custodian_profile_name: name, asset_name } = payload as ICustodianProviderPayload;
    return [{ text: `Asset ${asset_name} removed from custodian profile ` }, linkPart(name, entryHref)];
  },
  custodian_operation_created: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Operation added to custodian profile ' }, linkPart(name, entryHref)];
  },
  custodian_operation_updated: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [
      { text: 'Operation on custodian profile ' },
      linkPart(name, entryHref),
      {
        text: ' updated',
      },
    ];
  },
  custodian_operation_removed: ({ payload, entryHref }) => {
    const { custodian_profile_name: name } = payload as ICustodianProviderPayload;
    return [{ text: 'Operation removed from custodian profile ' }, linkPart(name, entryHref)];
  },
  backup_created: ({ payload, entryHref }) => {
    const { backup_name, app_name = UNTITLED_APP_NAME } = payload as IBackupPayload;
    return [linkPart(backup_name, entryHref), { text: ` protection added to application ${app_name}` }];
  },
  backup_setup: ({ payload, entryHref }) => {
    const { backup_name } = payload as IBackupPayload;
    return [{ text: `Backup ` }, linkPart(backup_name, entryHref), { text: ' onboarding completed' }];
  },
  backup_removed: ({ payload }) => {
    const { backup_name } = payload as IBackupPayload;
    return `Backup ${backup_name} draft removed`;
  },
  backup_terminated: ({ payload, entryHref }) => {
    const { backup_name } = payload as IBackupPayload;
    return [
      { text: `Backup ` },
      linkPart(backup_name, entryHref),
      { text: ' removed and all connected assets permanently deleted' },
    ];
  },
  backup_activated: ({ payload, entryHref }) => {
    const { backup_name } = payload as IBackupPayload;
    return [{ text: `Backup ` }, linkPart(backup_name, entryHref), { text: ' activated' }];
  },
  backup_pending_activation: ({ payload, entryHref }) => {
    const { backup_name } = payload as IBackupPayload;
    return [{ text: `Backup ` }, linkPart(backup_name, entryHref), { text: ' activation initiated' }];
  },
  backup_updated: ({ payload, entryHref }) => {
    const { backup_name } = payload as IBackupPayload;
    return [{ text: `Settings for backup ` }, linkPart(backup_name, entryHref), { text: ' updated' }];
  },
  backup_asset_created: ({ payload, entryHref }) => {
    const { backup_name, asset_name } = payload as IBackupPayload;
    return [{ text: `Asset ${asset_name} added to backup ` }, linkPart(backup_name, entryHref)];
  },
  backup_asset_connected: ({ payload, entryHref }) => {
    const { backup_name, asset_name } = payload as IBackupPayload;
    return [{ text: `Asset ${asset_name} in backup ` }, linkPart(backup_name, entryHref), { text: ' sync connected' }];
  },
  backup_asset_disconnected: ({ payload, entryHref }) => {
    const { backup_name, asset_name } = payload as IBackupPayload;
    return [
      { text: `Asset ${asset_name} in backup ` },
      linkPart(backup_name, entryHref),
      { text: ' sync disconnected' },
    ];
  },
  backup_asset_removed: ({ payload, entryHref }) => {
    const { backup_name, asset_name } = payload as IBackupPayload;
    return [
      { text: `All versions of asset ${asset_name} in backup ` },
      linkPart(backup_name, entryHref),
      {
        text: ' permanently deleted',
      },
    ];
  },
  backup_version_downloaded: ({ payload, entryHref }) => {
    const { backup_name, asset_name } = payload as IBackupPayload;
    return [
      { text: `Download of asset ${asset_name} from backup ` },
      linkPart(backup_name, entryHref),
      {
        text: ' requested',
      },
    ];
  },
  backup_credential_created: ({ payload, entryHref }) => {
    const { backup_name, credential_name } = payload as IBackupPayload;
    return [{ text: `Asset ${credential_name} added to backup ` }, linkPart(backup_name, entryHref)];
  },
  backup_credential_removed: ({ payload, entryHref }) => {
    const { backup_name, credential_name } = payload as IBackupPayload;
    return [
      { text: `Asset ${credential_name} in backup ` },
      linkPart(backup_name, entryHref),
      {
        text: ' permanently deleted',
      },
    ];
  },
  backup_credential_downloaded: ({ payload, entryHref }) => {
    const { backup_name, credential_name } = payload as IBackupPayload;
    return [
      { text: `Download of asset ${credential_name} from backup ` },
      linkPart(backup_name, entryHref),
      {
        text: ' requested',
      },
    ];
  },
  backup_member_created: ({ payload, entryHref }) => {
    const { backup_name, member_name } = payload as IBackupPayload;
    return [{ text: `${member_name} added as member to backup ` }, linkPart(backup_name, entryHref)];
  },
  backup_member_updated: ({ payload, entryHref }) => {
    const { backup_name, member_name } = payload as IBackupPayload;
    return [
      { text: `Member permission for ${member_name} on backup ` },
      linkPart(backup_name, entryHref),
      { text: ' updated' },
    ];
  },
  backup_member_removed: ({ payload, entryHref }) => {
    const { backup_name, member_name } = payload as IBackupPayload;
    return [{ text: `${member_name} removed as a member from backup ` }, linkPart(backup_name, entryHref)];
  },
  backup_reports_enabled: ({ payload, entryHref }) => {
    const { backup_name } = payload as IBackupPayload;
    return [
      { text: 'Automated reporting on backup ' },
      linkPart(backup_name, entryHref),
      {
        text: ' enabled',
      },
    ];
  },
  backup_reports_disabled: ({ payload, entryHref }) => {
    const { backup_name } = payload as IBackupPayload;
    return [
      { text: 'Automated reporting on backup ' },
      linkPart(backup_name, entryHref),
      {
        text: ' disabled',
      },
    ];
  },
};

export const getLogIcon = (type: string | undefined) => {
  switch (type) {
    case 'software':
      return ImageEscrow;
    case 'saas':
      return ImageSaasEscrow;
    case 'continuity':
      return ImageContinuity;
    case 'CustodianProfile':
      return ImageCustodian;
    case 'Company':
    case 'App':
      return CompanyIcon;
    case 'Backup':
      return ImageBackups;
    default:
      return null;
  }
};

export const getAuditLogContent = ({
  eventType,
  payload,
  parentType,
  parentId,
}: {
  eventType: keyof typeof contentMap;
  payload: IPayload;
  parentType: string;
  parentId: string;
}): {
  parts: IPart[];
  icon: ReturnType<typeof getLogIcon>;
} | null => {
  const escrowName = [payload.depositor_company_name, payload?.beneficiary_company_name].filter(Boolean).join(' <> ');
  const resolvedParentType = parentType === 'Escrow' ? payload?.escrow_type : parentType;
  const entryHref = buildEntryHref(parentId, resolvedParentType);

  const entry = contentMap[eventType];
  if (!entry) return null;

  const ctx: ContentCtx = { escrowName, payload, entryHref };
  let raw: string | IPart[] | null;

  if (typeof entry === 'function') {
    raw = entry(ctx);
  } else {
    const variantFn = entry[resolvedParentType as ILogType] ?? entry.default;
    if (!variantFn) return null;
    raw = variantFn(ctx);
  }

  if (raw === null) return null;

  let parts: IPart[];
  if (Array.isArray(raw)) {
    parts = raw;
  } else {
    if (!raw.trim()) return null;
    parts = [{ text: raw }];
  }

  if (!parts.length) return null;

  return { parts, icon: getLogIcon(resolvedParentType) };
};
